import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, Eye, EyeOff, Save, X, Image, Film } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFAQs = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ question: '', answer: '', media: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', media: [] });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/admin/faqs`);
      setFaqs(res.data);
    } catch {
      // Seed defaults if empty
      await axios.post(`${API}/admin/faqs/seed`);
      const res = await axios.get(`${API}/admin/faqs`);
      setFaqs(res.data);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      toast.error('Both question and answer are required');
      return;
    }
    try {
      await axios.post(`${API}/admin/faqs`, newFaq);
      toast.success('FAQ created');
      setNewFaq({ question: '', answer: '', media: [] });
      setShowAdd(false);
      fetchFaqs();
    } catch {
      toast.error('Failed to create FAQ');
    }
  };

  const startEdit = (faq) => {
    setEditingId(faq.id);
    setEditData({ question: faq.question, answer: faq.answer, media: faq.media || [] });
  };

  const handleUpdate = async (faqId) => {
    if (!editData.question.trim() || !editData.answer.trim()) {
      toast.error('Both fields are required');
      return;
    }
    try {
      await axios.put(`${API}/admin/faqs/${faqId}`, editData);
      toast.success('FAQ updated');
      setEditingId(null);
      fetchFaqs();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleMediaUpload = (e, target) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    files.forEach(file => {
      const maxSize = file.type.startsWith('video/') ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max ${file.type.startsWith('video/') ? '20MB' : '5MB'}.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const mediaItem = {
          id: Date.now() + Math.random().toString(36).slice(2),
          type: file.type.startsWith('video/') ? 'video' : 'image',
          data: reader.result,
          name: file.name
        };
        if (target === 'new') {
          setNewFaq(prev => ({ ...prev, media: [...prev.media, mediaItem] }));
        } else {
          setEditData(prev => ({ ...prev, media: [...prev.media, mediaItem] }));
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeMedia = (mediaId, target) => {
    if (target === 'new') {
      setNewFaq(prev => ({ ...prev, media: prev.media.filter(m => m.id !== mediaId) }));
    } else {
      setEditData(prev => ({ ...prev, media: prev.media.filter(m => m.id !== mediaId) }));
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await axios.put(`${API}/admin/faqs/${faq.id}`, { active: !faq.active });
      toast.success(faq.active ? 'FAQ hidden' : 'FAQ visible');
      fetchFaqs();
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Delete this FAQ permanently?')) return;
    try {
      await axios.delete(`${API}/admin/faqs/${faqId}`);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleMoveUp = async (faq, index) => {
    if (index === 0) return;
    const prev = faqs[index - 1];
    try {
      await Promise.all([
        axios.put(`${API}/admin/faqs/${faq.id}`, { sort_order: prev.sort_order }),
        axios.put(`${API}/admin/faqs/${prev.id}`, { sort_order: faq.sort_order })
      ]);
      fetchFaqs();
    } catch {
      toast.error('Failed to reorder');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">Manage FAQs</h1>
          </div>
          <Button
            onClick={() => setShowAdd(!showAdd)}
            className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white text-sm"
            data-testid="add-faq-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add FAQ
          </Button>
        </div>

        {/* Add New FAQ */}
        {showAdd && (
          <Card className="p-4 mb-4 border-l-4 border-l-green-500" data-testid="new-faq-form">
            <h3 className="font-semibold text-gray-800 mb-3">New FAQ</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Question</label>
                <Input
                  value={newFaq.question}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="e.g. How do I track my shipment?"
                  data-testid="new-faq-question"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Answer</label>
                <textarea
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder="Type the answer here..."
                  className="w-full min-h-[100px] border border-gray-300 rounded-md p-3 text-sm resize-y"
                  data-testid="new-faq-answer"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Photos & Videos</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newFaq.media.map(m => (
                    <div key={m.id} className="relative group">
                      {m.type === 'image' ? (
                        <img src={m.data} alt={m.name} className="w-20 h-20 object-cover rounded border" />
                      ) : (
                        <div className="w-20 h-20 bg-gray-800 rounded border flex items-center justify-center">
                          <Film className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <button onClick={() => removeMedia(m.id, 'new')} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                      <p className="text-[10px] text-gray-500 truncate w-20 mt-0.5">{m.name}</p>
                    </div>
                  ))}
                </div>
                <label className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 cursor-pointer border border-blue-300 rounded px-3 py-1.5">
                  <Plus className="w-4 h-4" /> Add Media
                  <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => handleMediaUpload(e, 'new')} />
                </label>
                <p className="text-xs text-gray-400 mt-1">Images (max 5MB) and videos (max 20MB)</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700 text-white text-sm" data-testid="save-new-faq">
                  <Save className="w-4 h-4 mr-1" /> Save
                </Button>
                <Button variant="outline" onClick={() => { setShowAdd(false); setNewFaq({ question: '', answer: '', media: [] }); }} className="text-sm">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* FAQ List */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <Card key={faq.id} className={`p-4 ${!faq.active ? 'opacity-60 bg-gray-100' : ''}`} data-testid={`faq-card-${index}`}>
              {editingId === faq.id ? (
                /* Edit Mode */
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Question</label>
                    <Input
                      value={editData.question}
                      onChange={(e) => setEditData(prev => ({ ...prev, question: e.target.value }))}
                      data-testid={`edit-faq-question-${index}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Answer</label>
                    <textarea
                      value={editData.answer}
                      onChange={(e) => setEditData(prev => ({ ...prev, answer: e.target.value }))}
                      className="w-full min-h-[80px] border border-gray-300 rounded-md p-3 text-sm resize-y"
                      data-testid={`edit-faq-answer-${index}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Photos & Videos</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {editData.media.map(m => (
                        <div key={m.id} className="relative group">
                          {m.type === 'image' ? (
                            <img src={m.data} alt={m.name} className="w-20 h-20 object-cover rounded border" />
                          ) : (
                            <div className="w-20 h-20 bg-gray-800 rounded border flex items-center justify-center">
                              <Film className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <button onClick={() => removeMedia(m.id, 'edit')} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3" />
                          </button>
                          <p className="text-[10px] text-gray-500 truncate w-20 mt-0.5">{m.name}</p>
                        </div>
                      ))}
                    </div>
                    <label className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 cursor-pointer border border-blue-300 rounded px-3 py-1.5">
                      <Plus className="w-4 h-4" /> Add Media
                      <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => handleMediaUpload(e, 'edit')} />
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleUpdate(faq.id)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm">
                      <Save className="w-4 h-4 mr-1" /> Save
                    </Button>
                    <Button variant="outline" onClick={() => setEditingId(null)} className="text-sm">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div>
                  <div className="flex items-start gap-2">
                    <button onClick={() => handleMoveUp(faq, index)} className="text-gray-400 hover:text-gray-600 mt-1" title="Move up">
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 text-sm sm:text-base">{faq.question}</p>
                      <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{faq.answer}</p>
                      {faq.media && faq.media.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {faq.media.map(m => (
                            <div key={m.id}>
                              {m.type === 'image' ? (
                                <img src={m.data} alt={m.name} className="w-24 h-24 object-cover rounded border cursor-pointer hover:opacity-80" onClick={() => window.open(m.data, '_blank')} />
                              ) : (
                                <video src={m.data} controls className="w-40 h-24 rounded border" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {!faq.active && <span className="text-xs text-red-600 font-medium mt-1 inline-block">HIDDEN</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 pl-6">
                    <Button variant="outline" size="sm" onClick={() => startEdit(faq)} className="text-sm" data-testid={`edit-faq-${index}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleToggleActive(faq)} className="text-sm" data-testid={`toggle-faq-${index}`}>
                      {faq.active ? <><EyeOff className="w-3 h-3 mr-1" /> Hide</> : <><Eye className="w-3 h-3 mr-1" /> Show</>}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(faq.id)} className="text-red-600 hover:text-red-700 text-sm" data-testid={`delete-faq-${index}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>

        {faqs.length === 0 && (
          <Card className="p-8 text-center text-gray-500">
            <p>No FAQs yet. Click "Add FAQ" to create your first one.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminFAQs;
